/**
 * The CP step-up page at `/step-up/:id` (kernel apps, WS-9b, `design-docs/WS-9.md` section 3.6).
 *
 * `zion-app release` (WS-24) opens this link when release-engine answers `step_up_required`. The
 * link is the `stepUpUrl` of that 403 body, the `approveUrl` of WS-6, on the console origin (Q6).
 * The operator approves or denies the request here, and the terminal repeats its call.
 *
 * `CpAdminApp`'s `StepUpGate` renders this page chromeless, only for a signed-in operator who owes
 * no password change. A signed-out visit reaches `StashAndRedirectToLogin`, so the link survives the
 * sign-in.
 *
 * Security rules (`design-docs/WS-9.md` section 5): a malformed id sends no request (P-02). The page
 * reads no return URL and navigates nowhere after it settles (P-04).
 */
import { useState } from 'react';
import { isStepUpRequestId, type StepUpOutcome } from '../platform/auth/step-up';
import { STEP_UP_FORBIDDEN_TEXT, StepUpCard, type Authenticate } from '../platform/auth/StepUpDialog';
import { AuthCardFrame } from '../shell/AuthCardFrame';

/** The console path prefix of a step-up request. */
export const STEP_UP_PATH_PREFIX = '/step-up/';

/** The text of a link whose id is not a lower-case UUID. */
export const STEP_UP_INVALID_LINK_TEXT = 'This link is not a step-up request';

/** The text after each outcome. The page names the terminal, because the terminal opened the link. */
export const STEP_UP_PAGE_OUTCOME_TEXT: Readonly<Record<StepUpOutcome, string>> = {
  approved: 'Approved. Return to your terminal.',
  denied: 'Denied. The terminal gets a refusal.',
  expired: 'This request expired.',
  consumed: 'The operation already used this approval. Start it again.',
  cancelled: 'Cancelled. The request waits until it expires.',
};

/**
 * The request id of a step-up path, or `null`. The text after `STEP_UP_PATH_PREFIX` must be one
 * segment that `isStepUpRequestId` accepts, so `/step-up/<id>/x`, a trailing `/` and an upper-case
 * id answer `null`. Pure.
 */
export function stepUpIdFromPath(pathname: string): string | null {
  if (!pathname.startsWith(STEP_UP_PATH_PREFIX)) return null;
  const id = pathname.slice(STEP_UP_PATH_PREFIX.length);
  return isStepUpRequestId(id) ? id : null;
}

/** How the card settled for the page: the outcome, and whether a 403 refused this operator. */
interface Settled {
  readonly outcome: StepUpOutcome;
  readonly forbidden: boolean;
}

/**
 * The step-up page. With `requestId` `null` it shows `STEP_UP_INVALID_LINK_TEXT` and sends nothing.
 * With an id it renders `StepUpCard` (`cancelSendsDeny: false`) in `AuthCardFrame`, then the
 * `STEP_UP_PAGE_OUTCOME_TEXT` of the outcome. A 403 that settled the card shows
 * `STEP_UP_FORBIDDEN_TEXT` in place of the `denied` text, because nothing denied the request and the
 * terminal still waits (`design-docs/WS-9.md` section 3.4, S-06). It reads no return URL and
 * navigates nowhere.
 */
export function StepUpPage({
  requestId,
  authenticate,
}: {
  readonly requestId: string | null;
  readonly authenticate?: Authenticate;
}): React.JSX.Element {
  const [settled, setSettled] = useState<Settled | null>(null);

  if (requestId === null) {
    return (
      <AuthCardFrame
        title="Approve an operation"
        notice={{ verdict: 'bad', message: STEP_UP_INVALID_LINK_TEXT }}
        testId="step-up-page"
      />
    );
  }
  if (settled !== null) {
    const { outcome, forbidden } = settled;
    const notice = forbidden
      ? { verdict: 'bad' as const, message: STEP_UP_FORBIDDEN_TEXT }
      : { verdict: outcome === 'approved' ? ('good' as const) : ('caution' as const), message: STEP_UP_PAGE_OUTCOME_TEXT[outcome] };
    return <AuthCardFrame title="Approve an operation" notice={notice} autoFocusTitle testId="step-up-page" />;
  }
  return (
    <AuthCardFrame title="Approve an operation" testId="step-up-page">
      <StepUpCard
        requestId={requestId}
        onSettled={(outcome, forbidden) => setSettled({ outcome, forbidden })}
        authenticate={authenticate}
        cancelSendsDeny={false}
      />
    </AuthCardFrame>
  );
}
